<script>
import RestrictedSocialBookingDatesService from '@/services/RestrictedSocialBookingDatesService';
import { mapState } from 'vuex';

export default {
  name: 'RestrictedSocialDateModal',
  props: {
    visitLocations: {
      type: Array,
      required: true,
    },
    existing: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      restrictedDate: {
        visit_location_id: '',
        name: '',
        start_date: '',
        end_date: '',
        reason: '',
        social_table_count: 0,
      },
      submitting: false,
      updateSocialTableCount: false,
    };
  },
  computed: {
    ...mapState({
      prison: state => state.prison.prison,
    }),
    updating() {
      return this.existing;
    },
  },
  mounted() {
    if (this.existing) {
      this.restrictedDate = { ...this.existing };
      this.updateSocialTableCount = this.existing.social_table_count;
    }
  },
  methods: {
    submitForm() {
      if (this.existing) {
        this.updateDate();
      } else {
        this.addDate();
      }
    },
    addDate() {
      this.submitting = true;

      const startDateValue = new Date(this.restrictedDate.start_date);
      startDateValue.setUTCHours(0, 0, 0, 0);
      const endDateValue = new Date(this.restrictedDate.end_date);
      endDateValue.setUTCHours(23, 59, 59, 999);

      const dto = {
        visit_location_id: this.restrictedDate.visit_location_id,
        name: this.restrictedDate.name,
        start_date: startDateValue,
        end_date: endDateValue,
        reason: this.restrictedDate.reason,
        social_table_count: this.updateSocialTableCount ? Number(this.restrictedDate.social_table_count) || 0 : 0,
      };
      RestrictedSocialBookingDatesService.createDate(dto).then(() => {
        this.$toast.success('Restricted Date successfully added', {
          toastClassName: ['toast-std', 'success-toast'],
        });
        this.$emit('refresh', true);
      }).catch(err => {
        let message = 'Error creating the restricted Date, please try again';

        if (err.response && err.response.data && err.response.data.error === 'ENTITY_ALREADY_EXISTS') {
          message = 'Restricted Date already exists, please choose another';
        }
        this.$toast.error(message, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }).finally(() => {
        this.submitting = false;
      });
    },
    updateDate() {
      this.submitting = true;
      const dto = {
        name: this.restrictedDate.name,
        reason: this.restrictedDate.reason,
      };
      RestrictedSocialBookingDatesService.updateDate(this.existing._id, dto).then(() => {
        this.$toast.success('Restricted Date successfully updated', {
          toastClassName: ['toast-std', 'success-toast'],
        });
        this.$emit('refresh', true);
      }).catch(err => {
        let message = 'Error updating the restricted Date, please try again';

        if (err.response && err.response.data && err.response.data.error === 'ENTITY_ALREADY_EXISTS') {
          message = 'Restricted Date already exists, please choose another';
        }
        this.$toast.error(message, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }).finally(() => {
        this.submitting = false;
      });
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
  },
};
</script>

<template>
  <validation-observer ref="observer" v-slot="{ handleSubmit, invalid }">
    <b-form @submit.stop.prevent="handleSubmit(submitForm)">
      <b-row>
        <b-col v-if="!updating" cols="12">
          <validation-provider v-slot="validationContext" name="location" rules="required">
            <label>
              Location
            </label>
            <b-select
              id="location-input"
              v-model="restrictedDate.visit_location_id"
              :state="getValidationState(validationContext)"
              class="form-control form-control-sm"
              required
            >
              <option v-for="(location, index) in visitLocations" :key="index" :value="location._id">
                {{ location.name }}
              </option>
            </b-select>

          </validation-provider>
        </b-col>
        <b-col cols="12" class="mt-1">
          <validation-provider v-slot="validationContext" name="name" rules="required">
            <label>Name</label>
            <b-form-input
              v-model="restrictedDate.name"
              :state="getValidationState(validationContext)"
              cy="name"
              label="Name"
              placeholder="Enter name"
              required
            />
          </validation-provider>
        </b-col>
        <b-col v-if="!updating" cols="6" class="mt-1">
          <validation-provider v-slot="validationContext" name="startDate" rules="required">
            <label>Start Date</label>
            <b-form-datepicker
              v-model="restrictedDate.start_date"
              label="Start Date" cy="start-date"
              :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              type="date"
              :state="getValidationState(validationContext)"
              required
              @input="restrictedDate.end_date = null"
            />
          </validation-provider>
        </b-col>
        <b-col v-if="!updating" cols="6" class="mt-1">
          <validation-provider
            v-slot="validationContext"
            name="endDate"
            :rules="{required: true, after_or_equal_date: {startDate: restrictedDate.start_date}}"
          >
            <label>End Date</label>
            <b-form-datepicker
              v-model="restrictedDate.end_date" :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
              :min="restrictedDate.start_date"
              :state="getValidationState(validationContext)"
              cy="end-date"
              label="End Date"
              type="date"
            />
          </validation-provider>
        </b-col>
        <b-col v-if="!updating" cols="12" class="mt-1">
          <b-form-checkbox v-model="updateSocialTableCount">
            Update social table count?
          </b-form-checkbox>
        </b-col>
        <b-col v-if="updateSocialTableCount && !updating" cols="12" class="mt-1">
          <label>New Table Count (current maximum {{ prison.social_table_count }})</label>
          <b-form-input
            v-model="restrictedDate.social_table_count"
            type="number"
            placeholder="Enter a number you would like to reduce to"
          />
        </b-col>
        <b-col cols="12" class="mt-1">
          <label>Reason</label>
          <b-form-textarea
            v-model="restrictedDate.reason" label="Reason"
            cy="reason"
            placeholder="Enter reason"
            rows="4"
          />
        </b-col>
      </b-row>

      <section class="d-flex full-width mt-4">
        <div class="ui-spacer" />
        <button class="btn btn-danger mr-2" @click.prevent="$emit('close', true)">
          Cancel
        </button>
        <button
          class="btn btn-primary"
          :disabled="invalid"
        >
          {{ existing && existing._id ? 'Update' : 'Add' }} Restricted Date
        </button>
      </section>
    </b-form>
  </validation-observer>
</template>
